import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/lib/apiClient';

interface CoachingCallRow {
  transcriptId: string;
  callId: string;
  snippet: string | null;
  language: string;
  transcribedAt: string;
  topics: string[];
  objections: string[];
  actionItems: string[];
  sentiment: number | null;
  flags: string[];
}

const FLAG_LABEL_KEY: Record<string, string> = {
  long_monologue: 'flag_long_monologue',
  objection_not_handled: 'flag_objection_not_handled',
  next_action_discussed: 'flag_next_action_discussed',
};

function SentimentBar({ value }: { value: number | null }) {
  if (value === null) return null;
  const pct = Math.round(((value + 1) / 2) * 100);
  const color =
    value > 0.3 ? 'bg-green-500' : value < -0.3 ? 'bg-red-500' : 'bg-yellow-500';
  return (
    <div className="h-2 w-full rounded bg-muted">
      <div className={`h-2 rounded ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function CoachingCallsPage() {
  const { t } = useTranslation();
  const [calls, setCalls] = useState<CoachingCallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get('/api/v1/coaching/calls')
      .then((r) => setCalls(r.data))
      .catch(() => setCalls([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 p-6">
      <Breadcrumbs
        crumbs={[
          { label: t('coaching.title'), to: '/coaching/calls' },
          { label: t('coaching.calls_title') },
        ]}
      />
      <h1 className="text-2xl font-bold">{t('coaching.calls_title')}</h1>
      {loading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : calls.length === 0 ? (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground">{t('coaching.no_calls')}</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {calls.map((call) => {
            const isOpen = expanded === call.transcriptId;
            return (
              <Card key={call.transcriptId} className="p-4">
                <button
                  className="flex w-full items-start justify-between text-left"
                  onClick={() => setExpanded(isOpen ? null : call.transcriptId)}
                >
                  <div className="space-y-1">
                    <p className="font-medium text-sm">
                      {new Date(call.transcribedAt).toLocaleString()} &bull;{' '}
                      <span className="text-muted-foreground text-xs">{call.callId.slice(0, 8)}</span>
                    </p>
                    {call.flags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {call.flags.map((f) => (
                          <span
                            key={f}
                            className="rounded bg-amber-900/40 px-2 py-0.5 text-xs text-amber-200"
                          >
                            {t(`coaching.${FLAG_LABEL_KEY[f] ?? f}`)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="ml-2 text-muted-foreground text-xs">{isOpen ? '▲' : '▼'}</span>
                </button>

                {isOpen && (
                  <div className="mt-4 space-y-3 border-t border-border pt-3">
                    {call.snippet && (
                      <div>
                        <p className="text-xs font-semibold uppercase text-muted-foreground">
                          {t('coaching.transcript')}
                        </p>
                        <p className="mt-1 text-sm">{call.snippet}</p>
                      </div>
                    )}
                    {call.topics.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase text-muted-foreground">
                          {t('coaching.topics')}
                        </p>
                        <ul className="mt-1 list-disc pl-4 text-sm">
                          {call.topics.map((tp, i) => <li key={i}>{tp}</li>)}
                        </ul>
                      </div>
                    )}
                    {call.objections.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase text-muted-foreground">
                          {t('coaching.objections')}
                        </p>
                        <ul className="mt-1 list-disc pl-4 text-sm">
                          {call.objections.map((ob, i) => <li key={i}>{ob}</li>)}
                        </ul>
                      </div>
                    )}
                    {call.actionItems.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase text-muted-foreground">
                          {t('coaching.action_items')}
                        </p>
                        <ul className="mt-1 list-disc pl-4 text-sm">
                          {call.actionItems.map((ai, i) => <li key={i}>{ai}</li>)}
                        </ul>
                      </div>
                    )}
                    {call.sentiment !== null && (
                      <div>
                        <p className="text-xs font-semibold uppercase text-muted-foreground">
                          {t('coaching.sentiment')} ({call.sentiment.toFixed(2)})
                        </p>
                        <div className="mt-1">
                          <SentimentBar value={call.sentiment} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
